import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { usePageHead } from "@/lib/siteContent";
import { API } from "@/lib/api";

// Field-facing page from a fixture label QR: the crew confirms the cutout from their phone.
export default function FixtureConfirm() {
  const site = useSite();
  const { token } = useParams();
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  usePageHead({ title: f ? `Confirm — ${f.area}` : "Confirm fixture" });

  useEffect(() => {
    fetch(`${API}/public/fixture/${token}`)
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then(setF)
      .catch((e) => setError(e?.detail || "This label link is not valid"));
  }, [token]);

  const setConfirmed = async (confirmed) => {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/public/fixture/${token}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmed, by: name }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setF(data);
    } catch (e) {
      setError(e?.detail || "Could not save. Please try again.");
    }
    setBusy(false);
  };

  const field =
    "mt-1 w-full border border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-3 py-3 text-base text-[color:var(--s-ink)] outline-none focus:border-[color:var(--s-gold)]";
  const rows = f
    ? [
        ["Piece", f.piece],
        ["Mount", f.mount],
        ["Faucet holes", f.faucet_holes],
        ["Accessory holes", f.accessory_holes],
        ["Sink", f.sink],
        ["Tags", (f.tags || []).join(", ")],
        ["Notes", f.notes],
      ].filter(([, v]) => v)
    : [];

  return (
    <SiteShell site={site} testId="fixture-confirm">
      <main
        className="px-6 pb-24 pt-6 sm:pt-8 lg:px-12"
        data-testid="fixture-confirm-page"
      >
        <div className="mx-auto max-w-xl">
          <span className="s-micro inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
            Fixture / cutout · shop confirm
          </span>

          {error && !f && (
            <div
              className="mt-6 border border-red-300 bg-red-50 p-5 text-red-800"
              data-testid="fixture-confirm-error"
            >
              {error}
            </div>
          )}

          {f && (
            <>
              <h1
                className="s-display mt-4 text-3xl sm:text-4xl"
                data-testid="fixture-confirm-headline"
              >
                {f.area || "Fixture"}
              </h1>
              <p
                className="mt-2 text-[color:var(--s-mute)]"
                data-testid="fixture-confirm-sub"
              >
                {[f.project_name, f.customer_name, f.code]
                  .filter(Boolean)
                  .join(" · ")}
              </p>

              {f.warning && (
                <div
                  className="mt-4 flex items-center gap-2 border-2 border-red-600 bg-red-50 p-3 font-bold uppercase text-red-700"
                  data-testid="fixture-confirm-warning"
                >
                  <Warning size={18} weight="fill" /> {f.warning}
                </div>
              )}

              <div className="s-card mt-6 !p-5" data-testid="fixture-confirm-details">
                {rows.map(([k, v]) => (
                  <div
                    key={k}
                    className="flex gap-3 border-b border-[color:var(--s-line)] py-2 last:border-0"
                  >
                    <span className="s-micro w-28 shrink-0">{k}</span>
                    <span className="text-[color:var(--s-ink)]">{v}</span>
                  </div>
                ))}
              </div>

              {f.confirmed ? (
                <div
                  className="mt-6 border border-emerald-300 bg-emerald-50 p-6"
                  data-testid="fixture-confirm-done"
                >
                  <div className="flex items-center gap-3 text-emerald-800">
                    <CheckCircle size={28} weight="fill" />
                    <div className="text-lg font-semibold">Confirmed ready</div>
                  </div>
                  <p className="mt-2 text-emerald-900">
                    {f.confirmed_by ? `By ${f.confirmed_by}` : ""}
                    {f.confirmed_at ? ` · ${f.confirmed_at.slice(0, 10)}` : ""}
                  </p>
                  <button
                    type="button"
                    data-testid="fixture-unconfirm"
                    disabled={busy}
                    onClick={() => setConfirmed(false)}
                    className="s-ghost mt-4"
                  >
                    Mark not ready
                  </button>
                </div>
              ) : (
                <div className="mt-6" data-testid="fixture-confirm-form">
                  <label className="block text-sm font-semibold">
                    Your name (optional)
                    <input
                      data-testid="fixture-confirm-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Marco"
                      className={field}
                    />
                  </label>
                  {error && (
                    <p
                      className="mt-3 text-sm text-red-700"
                      data-testid="fixture-confirm-form-error"
                    >
                      {error}
                    </p>
                  )}
                  <button
                    type="button"
                    data-testid="fixture-confirm-submit"
                    disabled={busy}
                    onClick={() => setConfirmed(true)}
                    className="s-btn mt-5 w-full justify-center"
                  >
                    {busy ? "Saving…" : "Confirm this cutout is ready"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </SiteShell>
  );
}
